import React, {
    useMemo,
} from 'react'
import styles from 'styles/components/pages/order-quotes/[id]/confirmation-step.module.scss'
import Card from 'components/containers/card'
import useTranslate from 'helpers/hooks/useTranslate'
import {
    ConstrainMode,
    TooltipHost,
    DetailsListLayoutMode, DirectionalHint,
    TooltipDelay, DetailsRow, Icon, IconButton, SelectionMode, ShimmeredDetailsList,
    DefaultButton,
} from '@fluentui/react'
import Param from 'requests/objects/param'
import Divider from 'components/visuals/divider'
import User from 'requests/objects/user'
import OrderQuote from 'requests/objects/order-quote'
import OrderQuotesHandler from 'requests/handlers/orderQuotesHandler'
import WorkflowLight from 'components/pages/workflowLight'
import parseJson from 'helpers/methods/parseJson'
import Quotation from 'components/pages/order-quotes/[id]/quotation/quotation'
import { userProfilesWithSuperAdmin } from 'types/users/profiles'
import EWorkflowStep from 'types/orders/enums/workflowStep'
import { getLocaleFromLang } from 'helpers/methods/time'

/**
 * ConfirmationStep
 * @param {object} props Props
 * @param {object} props.lang Lang
 * @param {OrderQuote} props.item Item
 * @param {(newIndex: number) => void} props.setCurrentPageIndex setCurrentPageIndex
 * @param {Param} props.param param
 * @param {(order: Partial<OrderQuote>) => void} props.onChange onChange
 * @param {(arg0: import('redux/slices/common').PayloadMessageBar) => void} props.setMessageBar setMessageBar
 * @param {OrderQuotesHandler} props.orderQuotesHandler orderQuotesHandler
 * @param {boolean} props.userCanEdit userCanEdit
 * @param {User} props.me me
 * @param {Function} props.setupBreadcrumb setupBreadcrumb
 * @param {Function} props.setupCommandBar setupCommandBa
 * @returns {JSX.Element} Element
 */
export default function ConfirmationStep({
    lang, item, setCurrentPageIndex, param,
    onChange, setMessageBar, orderQuotesHandler, userCanEdit, me, setupCommandBar, setupBreadcrumb,
}) {
    // Format Adress
    const formatAddress = address => {
        if (!address)
            return ''
        return [address.street, address.zipCode, address.city, address.country?.code].filter(a => a).join(', ')
    }
    const { pickupAddress, deliveryAddress } = item
    const { tKey, tObj } = useTranslate({ lang })
    const locale = getLocaleFromLang(lang)
    const pickupColumns = useMemo(() => /** @type {import('@fluentui/react').IColumn[]} */([
        {
            key: 'ref',
            name: tKey('remittance'),
            fieldName: 'orderQuoteId',
            minWidth: 50,
            maxWidth: 90,
            isResizable: true,
        },
        {
            key: 'pickupAddress',
            name: tKey('pickupAddress'),
            minWidth: 200,
            maxWidth: 300,
            isResizable: true,
            className: styles['delivery-address-data-item'],
            // eslint-disable-next-line react/no-unstable-nested-components
            onRender: (/** @type {OrderQuote} */ quote) => (
                <>
                    <b>{quote.pickupAddress?.label || ''}</b>
                    <span>
                        {quote.pickupAddress?.street || ''}
                        {' '}
                        {quote.pickupAddress?.zipCode || ''}
                        {' '}
                        {quote.pickupAddress?.city || ''}
                        {' '}
                        {quote.pickupAddress?.country?.code || ''}
                    </span>
                </>
            ),
        },
        {
            key: 'pickupDate',
            name: tKey('pickupDate'),
            minWidth: 100,
            maxWidth: 150,
            isResizable: true,
            className: styles['delivery-address-data-item'],
            onRender: () => (item.pickupStartDate
                ? new Date(item.pickupStartDate).toLocaleDateString(locale)
                : ''),
        },
        {
            key: 'button',
            fieldName: 'button',
            minWidth: 250,
            maxWidth: 500,
            isResizable: true,
            // eslint-disable-next-line react/no-unstable-nested-components
            onRender: () => (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <div className={styles['delivery-action-buttons']}>
                        <TooltipHost
                            content={tKey('viewAddress')}
                            directionalHint={DirectionalHint.topCenter}
                            delay={TooltipDelay.long}
                            styles={{ root: { margin: '-6px 0' } }}
                        >
                            <IconButton
                                iconProps={{
                                    iconName: 'SearchNearby',
                                }}
                                onClick={() => {
                                    // const origin = encodeURIComponent(formatAddress(pickupAddress))
                                    // const url = `https://www.google.com/maps/search/?api=1&query=${pickupAddress.lat}, ${pickupAddress.lon}`
                                    // streetVieew
                                    const url = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${pickupAddress.lat},${pickupAddress.lon}`
                                    // const url = `https://www.google.com/maps/search/?api=1&query=${pickupAddress.lat}, ${pickupAddress.lon} (${origin})`
                                    window.open(url, '_blank')
                                }}
                            />
                        </TooltipHost>
                        <TooltipHost
                            content={tKey(userCanEdit ? 'editPickup' : 'seeMore')}
                            directionalHint={DirectionalHint.topCenter}
                            delay={TooltipDelay.long}
                            styles={{ root: { margin: '-6px 0' } }}
                        >
                            <IconButton
                                iconProps={{
                                    iconName: userCanEdit ? 'Edit' : 'ReadingMode',
                                }}
                                onClick={() => setCurrentPageIndex(0)}
                            />
                        </TooltipHost>
                        <IconButton
                            disabled
                            styles={{
                                root: { color: 'transparent', backgroundColor: 'transparent' },
                                rootDisabled: { margin: '-6px 0', color: 'transparent', backgroundColor: 'transparent' },
                            }}
                        />
                    </div>
                </div>
            ),
        },
    ]), [item.pickupStartDate, locale, pickupAddress?.lat, pickupAddress?.lon, setCurrentPageIndex, tKey, userCanEdit])

    const columns = useMemo(() => /** @type {import('@fluentui/react').IColumn[]} */([
        {
            key: 'deliveryAddress',
            name: tKey('deliveryAddress'),
            fieldName: 'deliveryAddress',
            minWidth: 200,
            maxWidth: 300,
            isResizable: true,
            className: styles['delivery-address-data-item'],
            // eslint-disable-next-line react/no-unstable-nested-components
            onRender: (/** @type {OrderQuote} */ data) => (
                <>
                    <b>{data.deliveryAddress?.label || ''}</b>
                    <span>
                        {data.deliveryAddress?.street || ''}
                        {' '}
                        {data.deliveryAddress?.zipCode || ''}
                        {' '}
                        {data.deliveryAddress?.city || ''}
                        {' '}
                        {data.deliveryAddress?.country?.code || ''}
                    </span>
                </>
            ),
        },

        {
            key: 'deliveryDate',
            name: tKey('deliveryDate'),
            fieldName: 'deliveryDate',
            minWidth: 100,
            maxWidth: 150,
            isResizable: true,
            className: styles['delivery-address-data-item'],
            onRender: data => (data.deliveryStartDate
                ? new Date(data.deliveryStartDate).toLocaleDateString(locale)
                : ''),
        },
        {
            key: 'content',
            name: tKey('merchandises'),
            fieldName: 'content',
            minWidth: 400,
            isResizable: true,
            // eslint-disable-next-line react/no-unstable-nested-components
            onRender: data => {
                if (data.orderQuoteId && !data.merchandiseId)
                    return (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span
                                onKeyDown={() => null}
                                role="button"
                                tabIndex={0}
                                style={{
                                    cursor: 'initial', display: 'block', margin: '-11px', padding: '11px',
                                }}
                            >
                                <Icon
                                    iconName="ChevronDown"
                                />
                                {' '}
                                {data.merchandises?.length ?? 0}
                                {' '}
                                (
                                {tKey('handlingUnit')}
                                )
                            </span>
                        </div>
                    )

                if (data.merchandiseId || data.isManualArticle)
                    return (
                        <span className={styles['marchandise-article-data']}>
                            <span className={styles['article-line']}>
                                {data.quantity}
                                {' '}
                                {data.article ? data.article.name : data.merchandiseName}

                                {' - '}
                                Ref:
                                {data.reference}

                                {' - '}
                                {data.footage}
                                {' '}
                                {tKey('linearMeterSuffix').toLocaleLowerCase()}

                                {' - '}
                                {data.weight}
                                {' '}
                                kg

                                {(() => {
                                    const dims = [
                                        Number(data.long),
                                        Number(data.width),
                                        Number(data.height),
                                    ].filter(d => d > 0)

                                    if (dims.length < 2)
                                        return null

                                    return (
                                        <>
                                            {' - '}
                                            {dims.join(' x ')}
                                            {' '}
                                            cm
                                        </>
                                    )
                                })()}
                            </span>
                        </span>

                    )
                return null
            },
        },
        {
            key: 'button',
            name: '',
            fieldName: 'button',
            minWidth: 50,
            maxWidth: 80,
            isResizable: false,
            // eslint-disable-next-line react/no-unstable-nested-components
            onRender: (/** @type {OrderQuote} */ data) => {
                const delivery = data.deliveryAddress
                if (!delivery)
                    return null
                // const url = `https://www.google.com/maps/search/?api=1&query=${pickupAddress.lat}, ${pickupAddress.lon}`
                // street View
                const url = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${delivery.lat},${delivery.lon}`

                return (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <TooltipHost
                            content={tKey('viewAddress')}
                            directionalHint={DirectionalHint.topCenter}
                            delay={TooltipDelay.long}
                            styles={{ root: { margin: '-6px 0' } }}
                        >
                            <IconButton
                                iconProps={{ iconName: 'SearchNearby' }}
                                onClick={() => window.open(url, '_blank')}
                            />
                        </TooltipHost>
                        <IconButton
                            disabled
                            styles={{
                                root: { color: 'transparent', backgroundColor: 'transparent' },
                                rootDisabled: { margin: '-6px 0', color: 'transparent', backgroundColor: 'transparent' },
                            }}
                        />
                        <IconButton
                            disabled
                            styles={{
                                root: { color: 'transparent', backgroundColor: 'transparent' },
                                rootDisabled: { margin: '-6px 0', color: 'transparent', backgroundColor: 'transparent' },
                            }}
                        />

                    </div>
                )
            },
        },

        // eslint-disable-next-line max-len
    ]), [locale, tKey])

    /** Item to be displayed */
    const items = useMemo(() => {
        const res = []
        res.push(item)

        for (const merchandise of item.merchandises) // eslint-disable-line no-restricted-syntax
            res.push(merchandise)

        return res
    }, [item])

    const isAdmin = userProfilesWithSuperAdmin.includes(me.profileId)

    return (
        <div className={styles['confirmation-step']}>
            <WorkflowLight
                steps={item.workflowHistorics
                    .map(wfh => ({
                        label: tObj(parseJson(wfh.workflowStep.text)),
                        isCompleted: wfh.isCompleted,
                        isActive: wfh.isActive,
                        isError: wfh.error,
                    }))}
            />

            <Card
                className={styles['confirmation-summary']}
                title={tKey('summary')}
                iconName="BulletedList2"
                hasDividerMargin={false}
            >
                <ShimmeredDetailsList
                    items={[item]}
                    columns={pickupColumns}
                    selectionMode={SelectionMode.none}
                    onShouldVirtualize={() => true}
                    layoutMode={DetailsListLayoutMode.justified}
                    constrainMode={ConstrainMode.unconstrained}
                />
                <br />
                <Divider
                    style={{
                        marginBottom: '0',
                    }}
                />
                <ShimmeredDetailsList
                    items={items}
                    columns={columns}
                    selectionMode={SelectionMode.none}
                    onShouldVirtualize={() => true}
                    layoutMode={DetailsListLayoutMode.justified}
                    constrainMode={ConstrainMode.unconstrained}
                    enableUpdateAnimations
                    // eslint-disable-next-line react/no-unstable-nested-components
                    onRenderDetailsFooter={detailsFooterProps => (
                        <DetailsRow
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            {...detailsFooterProps}
                            columns={detailsFooterProps.columns.map(x => ({ ...x, onRender: null }))}
                            item={{ content: {} }}
                            itemIndex={-1}
                            groupNestingDepth={detailsFooterProps.groupNestingDepth}
                            selectionMode={SelectionMode.none}
                            selection={detailsFooterProps.selection}
                            // eslint-disable-next-line react/no-unstable-nested-components
                            onRenderItemColumn={(_, _index, column) => {
                                switch (column.key) {
                                    case 'index':
                                        return (
                                            <span style={{ color: 'rgb(50, 49, 48)', fontSize: '13px', fontWeight: 600 }}>
                                                {tKey('total')}
                                            </span>
                                        )
                                    case 'ref':
                                        return null
                                    case 'deliveryAddress':
                                        return (
                                            <span style={{ color: 'rgb(50, 49, 48)', fontSize: '13px', fontWeight: 600 }}>
                                                1 destination
                                            </span>
                                        )
                                    case 'content':
                                        return (
                                            <span style={{ color: 'rgb(50, 49, 48)', fontSize: '13px', fontWeight: 600 }}>
                                                1
                                                {tKey('HU')}
                                                <span
                                                    style={{ marginLeft: '6rem' }}
                                                />
                                                {item.merchandises
                                                    .flat().length ?? 0}
                                                {' '}
                                                {tKey('articles')}
                                            </span>
                                        )
                                    default:
                                        return null
                                }
                            }}
                        />
                    )}
                />
                {isAdmin && (
                    <div style={{ marginTop: '1rem', alignItems: 'center' }}>
                        <DefaultButton
                            text={tKey('viewDirectionsButton')}
                            styles={{}}
                            onClick={() => {
                                const origin = encodeURIComponent(formatAddress(pickupAddress))
                                const destination = encodeURIComponent(formatAddress(deliveryAddress))
                                const url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`
                                window.open(url)
                            }}
                        />
                    </div>
                )}

            </Card>
            <br />
            {item.currentWorkflowStepId !== EWorkflowStep.QuoteRequest && (
                <Quotation
                    item={item}
                    lang={lang}
                    me={me}
                    onChange={onChange}
                    orderQuotesHandler={orderQuotesHandler}
                    param={param}
                    setMessageBar={setMessageBar}
                    setupCommandBar={setupCommandBar}
                    setupBreadcrumb={setupBreadcrumb}
                />
            )}
        </div>
    )
}
